import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { FlexColumn } from 'src/components/flex';
import { useUpdatePropertyTourLinks } from 'src/services/api';
import PropertyTouringHoursSection, { type PropertyTouringHoursSectionRef } from 'src/components/property-touring-hours/PropertyTouringHoursSection';
import PropertyContactsSection from 'src/components/create-or-update-property-modal/PropertyContactsSection';
import PropertyIntegrationsSection from 'src/components/create-or-update-property-modal/PropertyIntegrationsSection';
import type { IPropertyWithIntegrations } from 'src/services/api/properties/use-property';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';
import styles from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal.module.css';
import { useNotifications } from 'src/notifications';

export interface PropertyTourSettingsSectionRef {
    applyAvailabilityChanges: () => Promise<boolean>;
}

interface Props {
    property: IPropertyWithIntegrations;
}

const PropertyTourSettingsSection = forwardRef<PropertyTourSettingsSectionRef, Props>(
    function PropertyTourSettingsSection({ property }, ref) {
        const { mutateAsync: updatePropertyTourLinks } = useUpdatePropertyTourLinks();
        const { addNotification } = useNotifications();
        const touringHoursRef = useRef<PropertyTouringHoursSectionRef>(null);
        const initialLink = property.showing_scheduling_link?.link || '';
        const [link, setLink] = useState(initialLink);
        const [savedLink, setSavedLink] = useState(initialLink);
        const [saveError, setSaveError] = useState<string | null>(null);
        const [isSaving, setIsSaving] = useState(false);

        useEffect(() => {
            setLink(initialLink);
            setSavedLink(initialLink);
            setSaveError(null);
        }, [initialLink]);

        const saveTourLink = useCallback(async () => {
            setSaveError(null);
            if (link === savedLink) {
                addNotification('Tour link is already up to date', 'success');
                return true;
            }

            setIsSaving(true);
            try {
                await updatePropertyTourLinks([{ id: property.id, link }]);
                setSavedLink(link);
                return true;
            } catch {
                setSaveError('Failed to save the tour link. Property was not updated.');
                return false;
            } finally {
                setIsSaving(false);
            }
        }, [addNotification, link, property.id, savedLink, updatePropertyTourLinks]);

        const applyAvailabilityChanges = useCallback(
            () => touringHoursRef.current?.applyChanges() ?? Promise.resolve(true),
            []
        );

        const isEliseIntegrationEnabled = useIsFeatureEnabled(EFeatureFlag.ELISE_AI_INTEGRATION);
        const hasIntegrations = isEliseIntegrationEnabled && (property.integrations ?? []).length > 0;

        useImperativeHandle(ref, () => ({ applyAvailabilityChanges }), [applyAvailabilityChanges]);

        return (
            <FlexColumn rowGap={2}>
                <Typography fontWeight={500} data-property-section-title>Tour Settings</Typography>

                {saveError && (
                    <Alert severity="error" data-testid="tour-settings-error-alert">
                        {saveError}
                    </Alert>
                )}

                <div className={styles.linkSaveRow}>
                    <TextField
                        label="Tour Link"
                        placeholder="https://calendly.com/name/property-tour"
                        value={link}
                        onChange={(event) => setLink(event.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        data-testid="tour-link-field"
                    />
                    <LoadingButton className={styles.sectionSaveButton} variant="contained" size="small" loading={isSaving} disabled={link === savedLink} onClick={saveTourLink} data-testid="save-tour-link-button">
                        Save
                    </LoadingButton>
                </div>

                {isEliseIntegrationEnabled && <PropertyIntegrationsSection property={property} />}

                <PropertyTouringHoursSection
                    ref={touringHoursRef}
                    property={property}
                    hideSchedule={hasIntegrations}
                />

                <PropertyContactsSection property={property} />
            </FlexColumn>
        );
    }
);

export default PropertyTourSettingsSection;
